import type { WidgetSettings, WidgetSpan } from '../../../store/dashboardStore';
import {
  changedCount, resolveItems, resolveOption, type WidgetItemSchema,
} from './widget-items';
import type { WidgetHeight } from './widget-chrome';
import { isDefaultQuickLinks, pickedQuickLinks } from './quick-link-order';

/**
 * 위젯 설정 창의 «바뀜» — 개수와 한 줄 요약 (2026-09-15 · 대시보드 시안 v2 V2 · 순수 함수).
 *
 * ★ 왼쪽 목록의 「바뀜 n」 · 미리보기 머리의 「본문 첫 줄 켬 · 표시 개수 3건」이 같은 셈을 쓴다.
 * ★ 높이 「보통」 · 제목 보이기는 기본값이다 — 옛 설정 창이 `height:'m'` 을 적어 두어도 바뀜으로 세지 않는다.
 * ★★ **폭은 세지 않는다.** 폭은 설정이 아니라 «배치»다 — 보드를 만들 때 위젯이 모두 1/3 로 놓여(`createBoard`)
 *    카탈로그 기본 폭과 다르다. 그것을 「바뀜」으로 세면 한 번도 안 건드린 위젯이 바뀐 것처럼 보인다.
 *    폭은 왼쪽 목록 줄에 그대로 적힌다.
 */

export interface WidgetDraft {
  title: string;
  showTitle: boolean;
  height: WidgetHeight;
  span: WidgetSpan;
  settings: WidgetSettings;
}

const HEIGHT_LABEL: Record<WidgetHeight, string> = { s: '낮게', m: '보통', l: '크게' };

/** 창을 닫을 때 «바꾼 것이 있나» — 키 순서 · undefined 칸에 흔들리지 않게 굳혀서 비교한다. */
export function stableJson(value: unknown): string {
  return JSON.stringify(value, (_k, v: unknown) => {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      return Object.fromEntries(Object.entries(v as Record<string, unknown>).filter(([, x]) => x !== undefined).sort(([a], [b]) => a.localeCompare(b)));
    }
    return v;
  });
}

export function sameDraft(a: WidgetDraft, b: WidgetDraft): boolean {
  return a.title.trim() === b.title.trim()
    && a.showTitle === b.showTitle
    && a.height === b.height
    && a.span === b.span
    && stableJson(a.settings) === stableJson(b.settings);
}

/** 기본값과 다른 칸 수. */
export function draftChangeCount(schema: WidgetItemSchema | null, defaultSpan: number, d: WidgetDraft): number {
  let n = 0;
  if (schema) {
    // 이름은 아래에서 센다 — 보이는 항목 · 선택지만 V1 규칙으로.
    n += changedCount(schema, { items: d.settings.items, options: d.settings.options });
  }
  if (d.title.trim() !== '') n += 1;
  if (d.height !== 'm') n += 1;
  if (!d.showTitle) n += 1;
  // v2 V4 — 빠른 실행의 바로가기(켜기 · 순서)가 기본 여덟과 다르면 하나로 센다.
  if (!isDefaultQuickLinks(d.settings.quickLinks)) n += 1;
  void defaultSpan;   // 폭은 세지 않는다(위 주석) — 부르는 쪽 모양은 그대로 둔다.
  return n;
}

/** 미리보기 머리 한 줄 — 앞의 셋만 적고 나머지는 「외 n」. 바뀐 것이 없으면 「기본값 그대로」. */
export function draftChangeSummary(schema: WidgetItemSchema | null, defaultSpan: number, d: WidgetDraft): string {
  const parts: string[] = [];
  if (schema) {
    for (const g of schema.groups) {
      const resolved = resolveItems(schema, d.settings, {}, g.key);
      for (const r of resolved) {
        if (r.on !== r.def.defaultOn) parts.push(`${r.def.label} ${r.on ? '켬' : '끔'}`);
      }
      const order = resolved.map((r) => r.def.key).join(',');
      const base = schema.items.filter((i) => i.group === g.key).map((i) => i.key).join(',');
      if (order !== base) parts.push(`${g.label} 순서 바꿈`);
    }
    for (const o of schema.options) {
      if (d.settings.options?.[o.key] === undefined) continue;
      const v = resolveOption(schema, d.settings, o.key, {
        scopes: { team: true, division: true, company: true },
      });
      if (o.kind === 'toggle') {
        if (v !== o.defaultValue) parts.push(`${o.label} ${v ? '켬' : '끔'}`);
      } else if (o.kind === 'single') {
        if (v !== o.defaultValue) parts.push(`${o.label} ${o.choices.find((c) => c.value === v)?.label ?? String(v)}`);
      } else if (Array.isArray(v) && [...v].sort().join() !== [...o.defaultValue].sort().join()) {
        parts.push(`${o.label} ${v.length}개`);
      }
    }
  }
  if (d.title.trim() !== '') parts.push(`이름 「${d.title.trim()}」`);
  void defaultSpan;
  if (d.height !== 'm') parts.push(`높이 ${HEIGHT_LABEL[d.height]}`);
  if (!d.showTitle) parts.push('제목 숨김');
  if (!isDefaultQuickLinks(d.settings.quickLinks)) parts.push(`바로가기 ${pickedQuickLinks(d.settings.quickLinks).length}개`);
  if (parts.length === 0) return '기본값 그대로';
  return parts.length > 3 ? `${parts.slice(0, 3).join(' · ')} 외 ${parts.length - 3}` : parts.join(' · ');
}
